import React, { ComponentType } from 'react';
import Slider from 'react-slick';
import { Icon, IconNames } from '@components/icon';
import type { LazyLoadTypes, Settings } from 'react-slick';

interface CarouselProps {
  children: React.ReactNode;
  showDots?: boolean;
  showArrows?: boolean;
  className?: string;
  responsive?: Settings['responsive'];
  onSlideChange?: (index: number) => void;
  loadingStrategy?: LazyLoadTypes;
}

const NextArrow = (props: any) => {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="absolute right-2 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 p-2 hover:bg-white"
    >
      <Icon name={IconNames.CHEVRON_RIGHT} className="h-4 w-4" />
    </button>
  );
};

const PrevArrow = (props: any) => {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="absolute left-2 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 p-2 hover:bg-white"
    >
      <Icon name={IconNames.CHEVRON_LEFT} className="h-4 w-4" />
    </button>
  );
};

// Cast Slider to ComponentType to resolve React JSX type conflicts
const SliderComponent = Slider as ComponentType<Settings>;

export const Carousel: React.FC<CarouselProps> = ({
  children,
  showDots = true,
  showArrows = true,
  className,
  responsive,
  onSlideChange,
  loadingStrategy = 'ondemand',
}) => {
  const settings: Settings = {
    dots: showDots,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: showArrows,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    adaptiveHeight: false,
    lazyLoad: loadingStrategy,
    dotsClass: 'slick-dots custom-dots',
    responsive,
    beforeChange: (_oldIndex: number, newIndex: number) => {
      onSlideChange?.(newIndex);
    },
  };

  return (
    <div className={`carousel-wrapper ${className || ''}`}>
      <SliderComponent {...settings}>{children as any}</SliderComponent>

      <style jsx global>{`
        .carousel-wrapper {
          aspect-ratio: 100/95;
        }
        .carousel-wrapper .slick-slider,
        .carousel-wrapper .slick-list,
        .carousel-wrapper .slick-track {
          height: 100%;
        }
        .carousel-wrapper .slick-slide > div {
          height: 100%;
        }
        .custom-dots {
          bottom: 8px !important;
          display: flex !important;
          justify-content: space-around;
          align-items: center;
          width: 100%;
          margin: 0;
          list-style: none;
          line-height: 1;
          overflow-x: auto;
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
          padding: 0 24px;
        }
        .custom-dots::-webkit-scrollbar {
          display: none; /* Chrome, Safari and Opera */
        }
        .custom-dots li {
          margin: 0 1px !important;
          display: inline-block;
          flex-shrink: 0;
        }
        .custom-dots li:first-child {
          margin-left: 16px !important;
        }
        .custom-dots li:last-child {
          margin-right: 16px !important;
        }
        .custom-dots li button:before {
          color: white !important;
          opacity: 0.5 !important;
          font-size: 5px !important;
        }
        .custom-dots li.slick-active button:before {
          opacity: 1 !important;
        }
      `}</style>
    </div>
  );
};
